import moment, { Moment } from 'moment';
import { ReturnPackage } from './return-package';
import { LnwshopSaleState } from './lnwshop-sale.service';

// Whether the units of a received package are already back in lnwshop
// stock — lnwshop undid the marketplace sale itself — or have to be keyed
// in by hand. Keying in a package lnwshop already restored counts it twice.
//
// lnwshop reacts to a platform event, not to the parcel reaching the shop.
// Measured on every return and failed delivery since June 2026 (live data,
// 2026-09-23):
//
//   failed delivery   restored, all three platforms: Lazada at
//                     shipped_back_success; Shopee and TikTok when the
//                     platform cancels the order (Shopee's cancel comes
//                     hours BEFORE the parcel is back)
//   Lazada return     restored only when EVERY unit of the order ends
//                     'returned', which happens at the refund (a median
//                     2.35 days after arrival); a partial return is not.
//                     A refund-only request is restored too although
//                     nothing comes back (phantom stock): Lazada decides
//                     refund-only itself when the seller does not answer in
//                     time — the reason the All Stores tab now flags
//                     requests awaiting the seller's decision
//   Shopee return     never — the order goes back to COMPLETED, so lnwshop
//                     has nothing to react to; nor for an in-transit refund
//   TikTok return     not since April 2026 (before that, lnwshop cancelled
//                     the whole order — a silent change that could revert)
//
// Because that behaviour has changed without notice before, the rule is
// only the expectation: what lnwshop's own crawl shows (LnwshopSaleService)
// wins. A sale the crawl shows undone is restored whatever the rule says,
// and a sale still standing in a crawl AFTER the event lnwshop should have
// reacted to is keyed in by hand.

export type RestockVerdict =
    // lnwshop undid the sale — confirmed by its crawl.
    | 'restored'
    // lnwshop is expected to undo the sale (the event has happened, or is
    // still to come), and no crawl since says otherwise.
    | 'expected'
    // lnwshop keeps the sale: the units have to be keyed in by hand.
    | 'manual';

export interface RestockAssessment {
    verdict: RestockVerdict;
    // One line for the page, in Thai.
    note: string;
}

// When the event lnwshop undoes the sale on happened: `at` null while it
// is still to come. `expected: false` when lnwshop never reacts.
type Trigger = { expected: false } | { expected: true; at: Moment | null };

const lazadaStatuses = (pkg: ReturnPackage): string[] =>
    pkg.platformStatus
        .split(' / ')
        .map((status) => status.trim())
        .filter(Boolean);

// Line statuses after which a Lazada return line never changes again.
const LAZADA_FINAL = ['REFUND_SUCCESS', 'REFUND_REJECT', 'REQUEST_CANCEL'];

// A Lazada return still waiting for its refund this long after its last
// change is not waiting any more (the seller has 3 days, Lazada refunds
// within about a week): the crawl decides from here on, so a status this
// code does not know can never hide a package for good.
const LAZADA_WAIT_DAYS = 14;

// How long lnwshop may take to react to the platform event before a sale
// still standing means it did not. Measured lag from a platform order to
// lnwshop's own record: Shopee p99 10 min, Lazada p99 21 min, TikTok p99
// 164 min (2026-09-23).
const LNWSHOP_REACTION_MARGIN_HOURS = 3;

export const restockTrigger = (pkg: ReturnPackage): Trigger => {
    if (pkg.source === 'failed_delivery') {
        if (pkg.returnType === 'IN_TRANSIT_REFUND') {
            return { expected: false };
        }
        switch (pkg.platform) {
            // shipped_back_success; the items' updated_at is its time.
            case 'lazada':
                return {
                    expected: true,
                    at: pkg.packageStage === 'delivered' ? pkg.updatedAt : null,
                };
            // The cancel, which is what createdAt records for both.
            case 'shopee':
            case 'tiktok':
                return { expected: true, at: pkg.createdAt };
        }
    }
    if (pkg.platform !== 'lazada') {
        return { expected: false };
    }
    // A Lazada return: the refund flips the units to 'returned'. A request
    // rejected or withdrawn never gets there.
    const statuses = lazadaStatuses(pkg);
    if (
        statuses.length > 0 &&
        statuses.every((s) => s === 'REFUND_REJECT' || s === 'REQUEST_CANCEL')
    ) {
        return { expected: false };
    }
    // Every line settled with at least one refunded — all of them, or some
    // refunded and the rest rejected / withdrawn. lnwshop reacts to the last
    // change if at all; a crawl after it still showing the sale standing
    // (a partial return) lists the package for keying in.
    if (
        statuses.length > 0 &&
        statuses.every((s) => LAZADA_FINAL.includes(s))
    ) {
        return { expected: true, at: pkg.updatedAt };
    }
    // A line still open: lnwshop will react at the refund — unless the wait
    // has gone on too long to be one.
    const stale =
        pkg.updatedAt !== null &&
        pkg.updatedAt.isBefore(moment().subtract(LAZADA_WAIT_DAYS, 'days'));
    return { expected: true, at: stale ? pkg.updatedAt : null };
};

const NEVER_NOTES: Record<string, string> = {
    shopee: 'lnwshop ไม่คืนสต็อกให้เองสำหรับการคืนสินค้า Shopee',
    tiktok: 'lnwshop ไม่คืนสต็อกให้เองสำหรับการคืนสินค้า TikTok',
    lazada: 'คำขอคืน Lazada ถูกปฏิเสธหรือยกเลิก — lnwshop ยังนับเป็นขาย',
};

const UNKNOWN_NOTES: Record<string, string> = {
    unavailable: ' (ตรวจข้อมูล lnwshop ไม่ได้)',
    no_order: ' (ไม่พบคำสั่งซื้อนี้ใน lnwshop)',
    no_sell_rows: ' (ไม่พบรายการขายใน lnwshop)',
    not_crawled: ' (ข้อมูล lnwshop ของสินค้านี้ไม่อัปเดต)',
};

const unknownSuffix = (sale: LnwshopSaleState | undefined): string =>
    sale?.kind === 'unknown' ? UNKNOWN_NOTES[sale.reason] : '';

export const assessRestock = (
    pkg: ReturnPackage,
    sale: LnwshopSaleState | undefined,
    crawledAt: Moment | null,
): RestockAssessment => {
    if (sale?.kind === 'reversed') {
        return {
            verdict: 'restored',
            note: 'lnwshop คืนสต็อกให้แล้ว (ยกเลิกการขายของคำสั่งซื้อนี้)',
        };
    }
    const trigger = restockTrigger(pkg);
    if (!trigger.expected) {
        const never =
            pkg.returnType === 'IN_TRANSIT_REFUND'
                ? 'lnwshop ไม่คืนสต็อกให้เองกรณีขอคืนเงินระหว่างขนส่ง'
                : NEVER_NOTES[pkg.platform];
        return {
            verdict: 'manual',
            note: `${never} — ต้องเพิ่มสต็อกเอง${unknownSuffix(sale)}`,
        };
    }
    // Expected, but the sale was still standing when scraped well after the
    // event: lnwshop did not react (a partial Lazada return, or a changed
    // connector), so the units have to be keyed in. The margin keeps an
    // event minutes before the crawl — lnwshop not caught up yet — from
    // reading as "did not react".
    if (
        sale?.kind === 'standing' &&
        trigger.at !== null &&
        crawledAt !== null &&
        sale.lastSeenAt.isAfter(
            trigger.at.clone().add(LNWSHOP_REACTION_MARGIN_HOURS, 'hours'),
        )
    ) {
        return {
            verdict: 'manual',
            note:
                pkg.platform === 'lazada' && pkg.source === 'return'
                    ? 'lnwshop ไม่ได้คืนสต็อก (Lazada คืนไม่ครบทั้งคำสั่งซื้อ) — ต้องเพิ่มสต็อกเอง'
                    : 'lnwshop ยังไม่ได้คืนสต็อกตามที่ควร — ต้องเพิ่มสต็อกเอง',
        };
    }
    return {
        verdict: 'expected',
        note:
            (trigger.at === null && pkg.source === 'return'
                ? 'lnwshop จะคืนสต็อกให้เมื่อ Lazada คืนเงินครบทั้งคำสั่งซื้อ'
                : 'lnwshop คืนสต็อกให้อัตโนมัติ — ยืนยันกับข้อมูล lnwshop หลังรอบดึงข้อมูลคืนนี้') +
            unknownSuffix(sale),
    };
};
